import * as React from "react"
import { cn } from "../../lib/utils"

export interface DateInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ className, placeholder = "Select date", onClick, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    
    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current!);
    
    const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
      // Open picker on any click within the input
      if (inputRef.current) {
        inputRef.current.showPicker?.();
      }
      onClick?.(e);
    };
    
    const handleIconClick = () => {
      if (inputRef.current) {
        inputRef.current.showPicker?.();
        inputRef.current.focus();
      }
    };
    
    return (
      <div className={cn("relative", className)}>
        <button
          type="button"
          onClick={handleIconClick}
          className="absolute inset-y-0 left-0 pl-4 flex items-center z-10 cursor-pointer hover:opacity-80"
          tabIndex={-1}
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
        <input
          type="date"
          ref={inputRef}
          placeholder={placeholder}
          onClick={handleClick}
          className={cn(
            "h-12 w-full rounded-lg border border-gray-300 bg-white pl-12 pr-4 py-2 text-sm text-gray-900 transition-all",
            "hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-aalaw-red focus:border-transparent",
            "cursor-pointer"
          )}
          style={{
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
          }}
          {...props}
        />
        <style>{`
          input[type="date"]::-webkit-datetime-edit {
            width: 100%;
          }
          input[type="date"]::-webkit-datetime-edit-fields-wrapper {
            width: 100%;
            justify-content: flex-start;
          }
          input[type="date"]::-webkit-calendar-picker-indicator {
            position: absolute;
            right: 12px;
            cursor: pointer;
            opacity: 0.6;
          }
          input[type="date"]::-webkit-calendar-picker-indicator:hover {
            opacity: 1;
          }
        `}</style>
      </div>
    )
  }
)
DateInput.displayName = "DateInput"

export { DateInput }
